/**
 * Top-level auth guard for the simpl_auth migration.
 *
 * Behaviour:
 *
 *   1. On mount, probe `/api/user` to see whether the caller already
 *      has a valid session — either a Cognito Authorization header
 *      (legacy bridge users) or the `SIMPL_AUTH_JWT_<env>` cookie
 *      (post-cutover users).
 *   2. While the probe is in flight, render `<loadingFallback>` (a
 *      branded splash) so we never flash any sign-in UI for users who
 *      already have a session.
 *   3. If the probe returns 401, the §6.7 Cognito bootstrap bridge
 *      silently exchanges any latent Amplify session for a
 *      `SIMPL_AUTH_JWT_<env>` cookie; the guard renders a small
 *      "Updating your sign-in…" splash during the round-trip so users
 *      never see the login UI flash.
 *   4. On `authed`: provide the caller via `<SimplAuthCallerContext>`
 *      and render `children`. Consumers (e.g. `useAuth`,
 *      `<DefaultHome>`, `<EulaGate>`) read identity from the context
 *      instead of `useAuthenticator()`, so the SPA works identically
 *      whether the user signed in via Cognito or simpl_auth.
 *   5. On `unauthed` (probe failed with 401 and the bridge had
 *      nothing to trade or was rejected): render `unauthedFallback`,
 *      which defaults to `<SignInRedirect>` — bouncing the browser to
 *      simpl_auth's `/authorize` flow. This is the ONLY status that
 *      redirects, because it's the only one where a fresh sign-in
 *      would actually change the input to the next probe.
 *   6. On `account-not-provisioned` / `forbidden` / `service-unavailable`
 *      (probe failed with something other than 401): render the
 *      corresponding error surface. These deliberately DO NOT
 *      redirect — signing in again cannot fix a missing user row, a
 *      forbidden JWT, or an API outage, and coercing them into
 *      `unauthed` (as this guard did pre-SE-1765) produced a tight
 *      redirect loop that only shell access could escape. Each
 *      surface exposes a Sign out button; `service-unavailable`
 *      additionally exposes Retry, which re-runs the probe via the
 *      hook's `retry()` handler.
 */

import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState
} from "react";
import { CircularProgress, Box, Typography, Button } from "@mui/material";
import { Authenticator, ThemeProvider } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import { Auth } from "aws-amplify";
import {
  cognitoAuthenticatorComponents,
  cognitoFormFields,
  cognitoAuthTheme,
  cognitoAuthServices
} from "./cognitoLoginBranding";

import { CallerClaims, Role } from "./roles";
import {
  AccessDeniedSurface,
  AccountNotProvisionedSurface,
  ServiceUnavailableSurface
} from "./SessionErrorSurface";
import { SignInRedirect } from "./SignInRedirect";
import {
  useSimplAuthSession,
  probeSimplAuthSession,
  SimplAuthSession
} from "./simplAuthSession";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { isCognitoAuthMode } from "./authMode";

type SimplAuthGuardProps = {
  /** Rendered when the session probe says we're authed. */
  children: ReactNode;
  /**
   * Rendered while the session probe is in flight or has not yet been
   * issued. Defaults to a centered MUI spinner — callers can pass a
   * branded splash if they want.
   */
  loadingFallback?: ReactNode;
  /**
   * Rendered while the §6.7 Cognito-exchange bridge is converting a
   * latent Amplify session into a `SIMPL_AUTH_JWT_<env>` cookie.
   * Defaults to a "Updating your sign-in…" splash; callers can
   * override with a custom UI.
   */
  bridgingFallback?: ReactNode;
  /**
   * Rendered when the session probe says we're unauthed. Defaults to
   * `<SignInRedirect>`, which bounces the browser to simpl_auth's
   * `/authorize` flow. Tests and embedded mounts can override with a
   * synchronous element (e.g. an inline error or a custom CTA).
   */
  unauthedFallback?: ReactNode;
  /**
   * Rendered when the whoami probe returned 404 (JWT valid, no `user`
   * row for the caller's email). Defaults to
   * `<AccountNotProvisionedSurface>`. Only override for tests /
   * storybook or for a customized message — DO NOT swap in a redirect
   * component, that's the pre-SE-1765 loop we fixed.
   */
  accountNotProvisionedFallback?: ReactNode;
  /**
   * Rendered when the whoami probe returned 403 (JWT valid, caller
   * rejected). Defaults to `<AccessDeniedSurface>`. Same caveat as
   * `accountNotProvisionedFallback` re: not swapping in a redirect.
   */
  forbiddenFallback?: ReactNode;
  /**
   * Rendered when the whoami probe hit a 5xx / network / timeout /
   * invalid-body condition. Defaults to `<ServiceUnavailableSurface>`
   * with the hook's `retry()` wired to the Retry button. Callers
   * overriding this are responsible for providing their own escape
   * hatch — the default surface's Sign out button is the only way
   * out of a bad session without shell access.
   */
  serviceUnavailableFallback?: ReactNode;
};

const CenteredSplash = ({ children }: { children?: ReactNode }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 2,
      minHeight: "100vh"
    }}
  >
    <CircularProgress />
    {children}
  </Box>
);

const DefaultLoadingFallback = () => <CenteredSplash />;
const DefaultBridgingFallback = () => (
  <CenteredSplash>
    <Typography variant="body1" color="text.secondary">
      Updating your sign-in…
    </Typography>
  </CenteredSplash>
);

/**
 * Caller payload published by `<SimplAuthGuard>` to its `children`
 * tree once the session probe settles on `authed`. Mirrors the
 * `caller` field on `/api/user` responses, which the
 * simpl-auth-authorizer Lambda populates from either a Cognito JWT
 * (`access_token` + `cognito:groups`) or a simpl_auth JWT (`roles`,
 * `program_ids`, etc.) — the SPA does not need to know which.
 */
export type SimplAuthCallerContextValue = {
  caller: CallerClaims;
  roles: Role[];
};

const SimplAuthCallerContext = createContext<SimplAuthCallerContextValue | null>(
  null
);

/**
 * Hook variant of `SimplAuthCallerContext` for consumers that want to
 * assert authed-ness at compile time. Throws if used outside an
 * authed `<SimplAuthGuard>` tree, which would indicate a routing
 * misconfiguration (e.g. a public-facing page accidentally calling a
 * caller-dependent hook).
 */
export const useSimplAuthCaller = (): SimplAuthCallerContextValue => {
  const ctx = useContext(SimplAuthCallerContext);
  if (!ctx) {
    throw new Error(
      "useSimplAuthCaller must be used inside an authed <SimplAuthGuard>"
    );
  }
  return ctx;
};

/**
 * Optional variant — returns `null` outside an authed guard. Use
 * this from components that may render in both authed and unauthed
 * contexts (e.g. shared shell elements).
 */
export const useOptionalSimplAuthCaller = ():
  | SimplAuthCallerContextValue
  | null => useContext(SimplAuthCallerContext);

const SimplAuthGuardViaSimplAuth = ({
  children,
  loadingFallback,
  bridgingFallback,
  unauthedFallback,
  accountNotProvisionedFallback,
  forbiddenFallback,
  serviceUnavailableFallback
}: SimplAuthGuardProps) => {
  const session = useSimplAuthSession();

  if (session.status === "loading" || session.status === "idle") {
    return <>{loadingFallback ?? <DefaultLoadingFallback />}</>;
  }

  if (session.status === "bridging") {
    return <>{bridgingFallback ?? <DefaultBridgingFallback />}</>;
  }

  // SE-787: helper that wraps a surface in `SimplAuthCallerContext` and
  // mounts the `ImpersonationBanner` above it whenever the probe gave
  // us a caller — including the error-state paths, where the target
  // user isn't provisioned locally but the impersonation trailer on
  // the JWT is still legitimate and needs a Clear/Exit affordance.
  // Without this hoist, an admin using QA impersonation would land on
  // AccountNotProvisionedSurface with no way to see (or exit) the
  // impersonation short of clearing cookies.
  const withCallerContext = (surface: ReactNode) => {
    if (!session.caller) return <>{surface}</>;
    const value: SimplAuthCallerContextValue = {
      caller: session.caller,
      roles: session.roles
    };
    return (
      <SimplAuthCallerContext.Provider value={value}>
        <ImpersonationBanner />
        {surface}
      </SimplAuthCallerContext.Provider>
    );
  };

  if (session.status === "authed" && session.caller) {
    return withCallerContext(children);
  }

  if (session.status === "account-not-provisioned") {
    return withCallerContext(
      accountNotProvisionedFallback ?? (
        <AccountNotProvisionedSurface probeStatus={session.probeStatus} />
      )
    );
  }

  if (session.status === "forbidden") {
    return withCallerContext(
      forbiddenFallback ?? (
        <AccessDeniedSurface probeStatus={session.probeStatus} />
      )
    );
  }

  if (session.status === "service-unavailable") {
    return withCallerContext(
      serviceUnavailableFallback ?? (
        <ServiceUnavailableSurface
          probeStatus={session.probeStatus}
          onRetry={session.retry}
        />
      )
    );
  }

  // Falls through only for `unauthed` — the one status where a fresh
  // sign-in materially changes the input to the next probe.
  return <>{unauthedFallback ?? <SignInRedirect />}</>;
};

/**
 * Cognito-mode guard for deployed envs WITHOUT a simpl_auth service
 * (dev, testone, testtwo, testthree, sadev, hsdev). Amplify's
 * `<Authenticator>` renders the Cognito sign-in UI and owns the session;
 * once the user is Cognito-authed we probe `/api/user` with the Cognito
 * ID token as a Bearer (the dual-issuer authorizer accepts the env's own
 * pool) to resolve the caller claims, then publish the SAME
 * `SimplAuthCallerContext` so the rest of the SPA is identical to
 * simpl_auth mode.
 */
const CognitoAuthedGate = ({
  children,
  loadingFallback
}: Pick<SimplAuthGuardProps, "children" | "loadingFallback">) => {
  const [session, setSession] = useState<SimplAuthSession>({
    status: "loading",
    caller: null,
    roles: []
  });

  useEffect(() => {
    let cancelled = false;
    // Bearer probe (skipCognitoBearer = false) — send the Cognito ID token.
    probeSimplAuthSession(false).then((s) => {
      if (!cancelled) setSession(s);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (session.status === "loading" || session.status === "idle") {
    return <>{loadingFallback ?? <DefaultLoadingFallback />}</>;
  }

  if (session.status === "authed" && session.caller) {
    const value: SimplAuthCallerContextValue = {
      caller: session.caller,
      roles: session.roles
    };
    return (
      <SimplAuthCallerContext.Provider value={value}>
        {children}
      </SimplAuthCallerContext.Provider>
    );
  }

  // Cognito-authed but no matching SIMPL user (email not in this env's DB).
  return (
    <CenteredSplash>
      <Typography variant="h6">No SIMPL account for this login</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, textAlign: "center" }}>
        You signed in successfully, but this email has no user record in this
        environment. Sign out and try a different account.
      </Typography>
      <Button
        variant="outlined"
        onClick={() => Auth.signOut().finally(() => window.location.reload())}
      >
        Sign out
      </Button>
    </CenteredSplash>
  );
};

const SimplAuthGuardViaCognito = ({
  children,
  loadingFallback
}: SimplAuthGuardProps) => (
  <ThemeProvider theme={cognitoAuthTheme}>
    <Authenticator
      components={cognitoAuthenticatorComponents}
      formFields={cognitoFormFields}
      services={cognitoAuthServices}
      hideSignUp
      className="Authenticator"
    >
      {() => (
        <CognitoAuthedGate loadingFallback={loadingFallback}>
          {children}
        </CognitoAuthedGate>
      )}
    </Authenticator>
  </ThemeProvider>
);

export const SimplAuthGuard = (props: SimplAuthGuardProps) =>
  isCognitoAuthMode() ? (
    <SimplAuthGuardViaCognito {...props} />
  ) : (
    <SimplAuthGuardViaSimplAuth {...props} />
  );
